import React, { createContext, useState, useContext, useEffect, useCallback } from 'react';
import axios from 'axios';
import { setStoredToken } from '../axios-config';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

  const checkAuth = useCallback(async () => {
    if (window.location.hash?.includes('session_id=')) {
      setLoading(false);
      return;
    }
    try {
      const { data } = await axios.get(`${API}/auth/me`, { withCredentials: true });
      setUser(data);
    } catch (error) {
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, [API]);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  const login = async (email, password) => {
    const { data } = await axios.post(`${API}/auth/login`, { email, password }, { withCredentials: true });
    if (data?.access_token) setStoredToken(data.access_token);
    setUser(data);
    return data;
  };

  const observerLogin = async (societyId, username, password) => {
    const { data } = await axios.post(`${API}/auth/observer-login`, { society_id: societyId, username, password }, { withCredentials: true });
    if (data?.access_token) setStoredToken(data.access_token);
    setUser(data);
    return data;
  };

  const register = async (formData) => {
    const { data } = await axios.post(`${API}/auth/register`, formData, { withCredentials: true });
    if (data?.access_token) setStoredToken(data.access_token);
    setUser(data);
    return data;
  };

  const logout = async () => {
    try { await axios.post(`${API}/auth/logout`, {}, { withCredentials: true }); } catch { /* ignore */ }
    setStoredToken(null);
    setUser(null);
  };

  return (
    <AuthContext.Provider value={{ user, setUser, loading, login, observerLogin, register, logout, checkAuth }}>
      {children}
    </AuthContext.Provider>
  );
};
